import { useState, useMemo, Component, type ReactNode } from "react";
import ReactDiffViewer, { DiffMethod } from "react-diff-viewer-continued";
import { Highlight, themes, type Language } from "prism-react-renderer";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./dialog";
import { useTheme } from "next-themes";

// Error boundary for syntax highlighting
class SyntaxHighlightBoundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode; fallback: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

// Map our language values to Prism language names
// Only includes languages supported by prism-react-renderer's default bundle
// Note: "text" maps to undefined to disable highlighting for plain text
const LANGUAGE_MAP: Record<string, Language | undefined> = {
  text: undefined, // No syntax highlighting for plain text
  javascript: "javascript",
  typescript: "typescript",
  python: "python",
  go: "go",
  rust: "rust",
  cpp: "cpp",
  c: "c",
  swift: "swift",
  kotlin: "kotlin",
  html: "markup",
  css: "css",
  sql: "sql",
  json: "json",
  yaml: "yaml",
  jsx: "jsx",
  tsx: "tsx",
  markdown: "markdown",
  graphql: "graphql",
  objectivec: "objectivec",
};

// Display limits
const DISPLAY_LIMITS = {
  diffLines: 5,
  codeLines: 10,
} as const;

interface DiffViewerProps {
  before: string;
  after?: string;
  language?: string;
  className?: string;
}

// Custom styles for inline unified diff - subtle line backgrounds, word-level highlighting
const customStyles = {
  variables: {
    light: {
      diffViewerBackground: "hsl(0 0% 100%)",
      diffViewerColor: "hsl(0 0% 9%)",
      // Very subtle line backgrounds
      addedBackground: "hsla(142, 69%, 45%, 0.05)",
      addedColor: "hsl(0 0% 20%)",
      removedBackground: "hsla(0, 72%, 50%, 0.05)",
      removedColor: "hsl(0 0% 20%)",
      // Word-level highlighting
      wordAddedBackground: "hsla(142, 69%, 45%, 0.35)",
      wordRemovedBackground: "hsla(0, 72%, 50%, 0.35)",
      addedGutterBackground: "hsla(142, 69%, 45%, 0.1)",
      removedGutterBackground: "hsla(0, 72%, 50%, 0.1)",
      gutterBackground: "hsl(0 0% 96%)",
      gutterBackgroundDark: "hsl(0 0% 94%)",
      codeFoldBackground: "hsl(0 0% 95%)",
      codeFoldGutterBackground: "hsl(0 0% 93%)",
      codeFoldContentColor: "hsl(0 0% 40%)",
      emptyLineBackground: "hsl(0 0% 100%)",
    },
    dark: {
      diffViewerBackground: "hsl(0 0% 7%)",
      diffViewerColor: "hsl(0 0% 98%)",
      // Very subtle line backgrounds
      addedBackground: "hsla(142, 69%, 58%, 0.05)",
      addedColor: "hsl(0 0% 90%)",
      removedBackground: "hsla(0, 72%, 63%, 0.05)",
      removedColor: "hsl(0 0% 90%)",
      // Word-level highlighting
      wordAddedBackground: "hsla(142, 69%, 58%, 0.35)",
      wordRemovedBackground: "hsla(0, 72%, 63%, 0.35)",
      addedGutterBackground: "hsla(142, 69%, 58%, 0.1)",
      removedGutterBackground: "hsla(0, 72%, 63%, 0.1)",
      gutterBackground: "hsl(0 0% 10%)",
      gutterBackgroundDark: "hsl(0 0% 8%)",
      codeFoldBackground: "hsl(0 0% 14.9%)",
      codeFoldGutterBackground: "hsl(0 0% 12%)",
      codeFoldContentColor: "hsl(0 0% 63.9%)",
      emptyLineBackground: "hsl(0 0% 7%)",
    },
  },
  line: {
    padding: "4px 8px",
    fontSize: "14px",
    fontFamily:
      'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
  },
  gutter: {
    minWidth: "36px",
    padding: "0 8px",
    fontSize: "12px",
  },
  contentText: {
    fontFamily:
      'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
  },
  diffContainer: {
    overflow: "hidden",
    pointerEvents: "none",
    userSelect: "none",
  },
};

// Hover card styles - neutral line backgrounds, word-level highlighting
const hoverCardStyles = {
  variables: {
    light: {
      diffViewerBackground: "hsl(0 0% 100%)",
      diffViewerColor: "hsl(0 0% 20%)",
      // Very subtle line backgrounds
      addedBackground: "hsla(142, 69%, 45%, 0.05)",
      addedColor: "hsl(0 0% 20%)",
      removedBackground: "hsla(0, 72%, 50%, 0.05)",
      removedColor: "hsl(0 0% 20%)",
      // Word-level highlighting
      wordAddedBackground: "hsla(142, 69%, 45%, 0.35)",
      wordRemovedBackground: "hsla(0, 72%, 50%, 0.35)",
      addedGutterBackground: "hsla(142, 69%, 45%, 0.1)",
      removedGutterBackground: "hsla(0, 72%, 50%, 0.1)",
      gutterBackground: "hsl(0 0% 96%)",
      gutterBackgroundDark: "hsl(0 0% 94%)",
      codeFoldBackground: "hsl(0 0% 95%)",
      codeFoldGutterBackground: "hsl(0 0% 93%)",
      codeFoldContentColor: "hsl(0 0% 40%)",
      emptyLineBackground: "hsl(0 0% 100%)",
    },
    dark: {
      diffViewerBackground: "hsl(0 0% 7%)",
      diffViewerColor: "hsl(0 0% 90%)",
      // Very subtle line backgrounds
      addedBackground: "hsla(142, 69%, 58%, 0.05)",
      addedColor: "hsl(0 0% 90%)",
      removedBackground: "hsla(0, 72%, 63%, 0.05)",
      removedColor: "hsl(0 0% 90%)",
      // Word-level highlighting
      wordAddedBackground: "hsla(142, 69%, 58%, 0.35)",
      wordRemovedBackground: "hsla(0, 72%, 63%, 0.35)",
      addedGutterBackground: "hsla(142, 69%, 58%, 0.1)",
      removedGutterBackground: "hsla(0, 72%, 63%, 0.1)",
      gutterBackground: "hsl(0 0% 10%)",
      gutterBackgroundDark: "hsl(0 0% 8%)",
      codeFoldBackground: "hsl(0 0% 14.9%)",
      codeFoldGutterBackground: "hsl(0 0% 12%)",
      codeFoldContentColor: "hsl(0 0% 63.9%)",
      emptyLineBackground: "hsl(0 0% 7%)",
    },
  },
  line: {
    padding: "6px 12px",
    fontSize: "14px",
    fontFamily:
      'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
  },
  gutter: {
    minWidth: "32px",
    padding: "0 8px",
    fontSize: "12px",
  },
  contentText: {
    fontFamily:
      'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
  },
  diffContainer: {
    overflow: "hidden",
  },
};

export function DiffViewer({
  before,
  after,
  language,
  className,
}: DiffViewerProps) {
  const { resolvedTheme } = useTheme();
  // Modal state for expanded view
  const [modalOpen, setModalOpen] = useState(false);

  // Determine if we should use dark theme (resolvedTheme handles "system" automatically)
  const isDark = resolvedTheme === "dark";

  // Get Prism language from our language value (undefined = no highlighting)
  const prismLanguage = LANGUAGE_MAP[language || "text"];

  // Syntax highlighting renderer for diff viewer
  // Returns plain text if no language is specified
  const highlightSyntax = useMemo(() => {
    if (!prismLanguage) {
      // No syntax highlighting for plain text
      return (str: string) => <>{str}</>;
    }
    return (str: string) => {
      // Safeguard against empty strings
      if (!str) return <>{str}</>;
      return (
        <SyntaxHighlightBoundary fallback={<>{str}</>}>
          <Highlight
            theme={isDark ? themes.nightOwl : themes.github}
            code={str}
            language={prismLanguage}
          >
            {({ tokens, getTokenProps }) => (
              <>
                {tokens.map((line, i) => (
                  <span key={i}>
                    {line.map((token, key) => (
                      <span key={key} {...getTokenProps({ token })} />
                    ))}
                  </span>
                ))}
              </>
            )}
          </Highlight>
        </SyntaxHighlightBoundary>
      );
    };
  }, [isDark, prismLanguage]);

  // Truncation logic
  const isDiff = !!after;
  const maxLines = isDiff ? DISPLAY_LIMITS.diffLines : DISPLAY_LIMITS.codeLines;

  const beforeLines = before.split("\n");
  const afterLines = after?.split("\n") ?? [];

  const beforeTruncated = beforeLines.length > maxLines;
  const afterTruncated = afterLines.length > maxLines;
  const isTruncated = beforeTruncated || afterTruncated;

  const displayBefore = beforeTruncated
    ? beforeLines.slice(0, maxLines).join("\n")
    : before;
  const displayAfter = afterTruncated
    ? afterLines.slice(0, maxLines).join("\n")
    : after;

  const hiddenLinesCount = Math.max(
    beforeLines.length - maxLines,
    afterLines.length - maxLines,
    0
  );

  // Truncation indicator component
  const TruncationIndicator = () => (
    <div className="px-3 py-1.5 bg-[hsl(var(--muted))] border border-t-0 border-[hsl(var(--border))] rounded-b-md text-xs text-[hsl(var(--muted-foreground))] font-mono">
      ⋯ {hiddenLinesCount} more line{hiddenLinesCount > 1 ? "s" : ""} hidden
    </div>
  );

  // Plain text fallback for code-only view
  const PlainCodePre = (
    <pre className="p-3 text-xs font-mono whitespace-pre w-fit">{displayBefore}</pre>
  );

  // Code-only view (no diff) with hover card for full preview
  if (!after) {
    // Full code content for hover card/modal
    const FullCodePre = (
      <pre className="p-3 overflow-x-auto text-xs font-mono">{before}</pre>
    );

    const codePreview = (
      <div className={`${className} cursor-pointer`}>
        <div
          className={`${
            isTruncated ? "rounded-t-md" : "rounded-md"
          } bg-[hsl(var(--card))] border border-[hsl(var(--border))] overflow-x-auto`}
        >
          {prismLanguage && displayBefore ? (
            <SyntaxHighlightBoundary fallback={PlainCodePre} key={`boundary-${isDark}`}>
              <Highlight
                key={`highlight-${isDark}`}
                theme={isDark ? themes.nightOwl : themes.github}
                code={displayBefore}
                language={prismLanguage}
              >
                {({ tokens, getLineProps, getTokenProps }) => (
                  <pre className="p-3 text-xs font-mono w-fit">
                    {tokens.map((line, i) => (
                      <div key={i} {...getLineProps({ line })} className="whitespace-pre">
                        {line.map((token, key) => (
                          <span key={key} {...getTokenProps({ token })} />
                        ))}
                      </div>
                    ))}
                  </pre>
                )}
              </Highlight>
            </SyntaxHighlightBoundary>
          ) : (
            PlainCodePre
          )}
        </div>
        {isTruncated && <TruncationIndicator />}
      </div>
    );

    // Click to open modal (all snippets are clickable)
    return (
      <>
        <div onClick={() => setModalOpen(true)}>
          {codePreview}
        </div>
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="sm:max-w-[900px] sm:max-h-[85vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="font-mono text-sm">Code</DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto rounded-md border border-[hsl(var(--border))] overflow-x-auto mr-2 sm:mr-0">
              {prismLanguage && before ? (
                <SyntaxHighlightBoundary fallback={FullCodePre} key={`modal-boundary-${isDark}`}>
                  <Highlight
                    key={`modal-highlight-${isDark}`}
                    theme={isDark ? themes.nightOwl : themes.github}
                    code={before}
                    language={prismLanguage}
                  >
                    {({ tokens, getLineProps, getTokenProps }) => (
                      <pre className="p-3 text-xs font-mono w-fit">
                        {tokens.map((line, i) => (
                          <div key={i} {...getLineProps({ line })} className="whitespace-pre">
                            {line.map((token, key) => (
                              <span key={key} {...getTokenProps({ token })} />
                            ))}
                          </div>
                        ))}
                      </pre>
                    )}
                  </Highlight>
                </SyntaxHighlightBoundary>
              ) : (
                FullCodePre
              )}
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Diff view preview
  const inlineDiff = (
    <div className={`${className} min-w-0 max-w-full cursor-pointer`}>
      <div
        className={`${
          isTruncated ? "rounded-t-md" : "rounded-md"
        } overflow-hidden border border-[hsl(var(--border))] [&_table]:table-fixed [&_table]:w-full`}
      >
        <ReactDiffViewer
          oldValue={displayBefore}
          newValue={displayAfter || ""}
          splitView={false}
          useDarkTheme={isDark}
          compareMethod={DiffMethod.WORDS_WITH_SPACE}
          styles={customStyles}
          hideLineNumbers={true}
          showDiffOnly={false}
          renderContent={highlightSyntax}
        />
      </div>
      {isTruncated && <TruncationIndicator />}
    </div>
  );

  // Click to open modal with side-by-side diff (both mobile and desktop)
  return (
    <>
      <div onClick={() => setModalOpen(true)}>
        {inlineDiff}
      </div>
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:w-fit sm:min-w-[900px] sm:max-w-[95vw] lg:max-w-[1400px] sm:max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-mono text-sm">Diff</DialogTitle>
          </DialogHeader>
          {/* Mobile: vertical layout */}
          <div className="flex-1 overflow-y-auto sm:hidden space-y-4 pr-2">
            {/* Before section */}
            <div>
              <div className="text-xs text-[hsl(var(--muted-foreground))] mb-2 font-mono">
                Before
              </div>
              <div className="rounded-md overflow-x-auto border border-[hsl(var(--border))] bg-[hsla(0,72%,50%,0.05)]">
                {prismLanguage && before ? (
                  <SyntaxHighlightBoundary
                    key={`mobile-before-boundary-${isDark}`}
                    fallback={
                      <pre className="p-3 text-sm font-mono whitespace-pre w-fit">
                        {before}
                      </pre>
                    }
                  >
                    <Highlight
                      key={`mobile-before-highlight-${isDark}`}
                      theme={isDark ? themes.nightOwl : themes.github}
                      code={before}
                      language={prismLanguage}
                    >
                      {({ tokens, getLineProps, getTokenProps }) => (
                        <pre className="p-3 text-sm font-mono w-fit">
                          {tokens.map((line, i) => (
                            <div key={i} {...getLineProps({ line })} className="whitespace-pre">
                              {line.map((token, key) => (
                                <span
                                  key={key}
                                  {...getTokenProps({ token })}
                                />
                              ))}
                            </div>
                          ))}
                        </pre>
                      )}
                    </Highlight>
                  </SyntaxHighlightBoundary>
                ) : (
                  <pre className="p-3 text-sm font-mono whitespace-pre w-fit">
                    {before}
                  </pre>
                )}
              </div>
            </div>
            {/* After section */}
            <div>
              <div className="text-xs text-[hsl(var(--muted-foreground))] mb-2 font-mono">
                After
              </div>
              <div className="rounded-md overflow-x-auto border border-[hsl(var(--border))] bg-[hsla(142,69%,45%,0.05)]">
                {prismLanguage && after ? (
                  <SyntaxHighlightBoundary
                    key={`mobile-after-boundary-${isDark}`}
                    fallback={
                      <pre className="p-3 text-sm font-mono whitespace-pre w-fit">
                        {after}
                      </pre>
                    }
                  >
                    <Highlight
                      key={`mobile-after-highlight-${isDark}`}
                      theme={isDark ? themes.nightOwl : themes.github}
                      code={after}
                      language={prismLanguage}
                    >
                      {({ tokens, getLineProps, getTokenProps }) => (
                        <pre className="p-3 text-sm font-mono w-fit">
                          {tokens.map((line, i) => (
                            <div key={i} {...getLineProps({ line })} className="whitespace-pre">
                              {line.map((token, key) => (
                                <span
                                  key={key}
                                  {...getTokenProps({ token })}
                                />
                              ))}
                            </div>
                          ))}
                        </pre>
                      )}
                    </Highlight>
                  </SyntaxHighlightBoundary>
                ) : (
                  <pre className="p-3 text-sm font-mono whitespace-pre w-fit">
                    {after}
                  </pre>
                )}
              </div>
            </div>
          </div>
          {/* Desktop: side-by-side diff */}
          <div className="hidden sm:block flex-1 overflow-y-auto">
            <div className="text-xs text-[hsl(var(--muted-foreground))] mb-2 flex">
              <span className="flex-1">Before</span>
              <span className="flex-1 text-right">After</span>
            </div>
            <div className="rounded-md overflow-hidden border border-[hsl(var(--border))] overflow-y-auto [&_table]:border-separate [&_table]:border-spacing-0 [&_td:nth-child(2)]:border-r [&_td:nth-child(2)]:border-[hsl(var(--border))]">
              <ReactDiffViewer
                oldValue={before}
                newValue={after}
                splitView={true}
                useDarkTheme={isDark}
                compareMethod={DiffMethod.WORDS_WITH_SPACE}
                styles={hoverCardStyles}
                hideLineNumbers={true}
                showDiffOnly={false}
                renderContent={highlightSyntax}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
